import { Box, Button, Stack, Typography } from "@mui/joy";
import { ReactNode } from "react";
import LanguageSelector from "../components/LanguageSelector";
import LinkzSVG from "../components/Icons/LinkzSVG";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { theme } from "../components/Theme";
import { If } from "../components/Condition";

type Props = {
  mode: "signin" | "signup";
  children: ReactNode;
};

const Onboarding = ({ mode, children }: Props) => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  return (
    <Stack direction={"row"}>
      {/* illustration section */}
      <Box
        sx={{
          display: { xs: "none", lg: "block" },
          px: "32px",
          pt: "32px",
          pb: "50px",
          width: "60%",
          height: "904px",
          backgroundImage: `url(/onboarding-background.png)`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundOrigin: "content-box",
          backgroundClip: "content-box",
          position: "relative",
        }}
      >
        {/* navigation bar lg */}
        <Stack
          direction={"row"}
          sx={{
            justifyContent: "space-between",
            width: "100%",
            mt: "39px",
            px: "45px",
          }}
        >
          <Link to={"/"}>
            <LinkzSVG />
          </Link>

          {/* language selector */}
          <Stack direction={"row"} gap={"20px"}>
            <LanguageSelector />

            <If
              condition={
                pathname.endsWith("signup") || pathname.endsWith("signin")
              }
            >
              <Link to={"https://www.linkzasia.com"}>
                <Button
                  variant="linkz-outlined-primary"
                  className="bg-transparent hover:bg-transparent"
                  startDecorator={<ArrowLeftIcon width={20} height={20} />}
                >
                  {t("landing.backToWebsiteBtn")}
                </Button>
              </Link>
            </If>
          </Stack>
        </Stack>

        {/* foreground illustration */}
        <img
          src={
            mode === "signin"
              ? "/signin-foreground.png"
              : "/signup-foreground.png"
          }
          style={{
            position: "absolute",
            left: 0,
            bottom: mode === "signin" ? 50 : -29,
            objectFit: "contain",
          }}
        />

        {/* testimony */}
        <Stack
          direction={"column"}
          sx={{
            gap: "23px",
            width: "370px",
            position: "absolute",
            top: "322px",
            left: "398px",
          }}
        >
          <Typography
            level="text-xl2-bold"
            sx={{ color: theme.palette.common.black }}
          >
            {t(`landing.testimonies.${mode}.testimonyTitle`)}
          </Typography>

          <Stack
            direction={"column"}
            sx={{
              gap: "16px",
              pl: "24px",
              borderLeft: `5px solid ${theme.palette.others.testimony_box_border}`,
            }}
          >
            {/* content */}
            <Typography
              level="text-md-medium"
              sx={{
                color: theme.palette.others.testimony_content_font,
                textAlign: "left",
                maxWidth: "328px",
              }}
            >
              {t(`landing.testimonies.${mode}.content`)}
            </Typography>

            {/* person details */}
            <Box>
              {/* name - title */}
              <Stack
                direction={"row"}
                sx={{
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <Typography
                  level="text-xl-bold"
                  textColor={theme.palette.common.black}
                >
                  {t(`landing.testimonies.${mode}.name`)}
                </Typography>

                <Typography
                  level="text-xl-bold"
                  textColor={theme.palette.common.black}
                >
                  -
                </Typography>

                <Typography
                  level="text-md-regular"
                  textColor={theme.palette.others.testimony_content_font}
                >
                  {t(`landing.testimonies.${mode}.title`)}
                </Typography>
              </Stack>

              {/* company */}
              <Typography
                level="text-lg-semibold"
                textColor={theme.palette.primary[500]}
              >
                {t(`landing.testimonies.${mode}.company`)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* form section */}
      <Box
        sx={{
          pt: "32px",
          pl: { xs: 0, md: "18px" },
          pr: { xs: 0, md: "50px" },
          width: { xs: "100%", lg: "40%" },
        }}
      >
        {/* mobile language selector, logo */}
        <Stack
          direction={"column"}
          sx={{
            display: { xs: "flex", lg: "none" },
            width: "100%",
            p: "16px",
            gap: "16px",
          }}
        >
          <Stack
            direction={"row"}
            sx={{
              justifyContent: "flex-end",
            }}
          >
            <LanguageSelector />
          </Stack>

          <Stack
            direction={"row"}
            sx={{
              display:
                pathname.endsWith("signup") || pathname.endsWith("signin")
                  ? "flex"
                  : "none",
              justifyContent: "center",
            }}
          >
            <Link to={"/"}>
              <LinkzSVG />
            </Link>
          </Stack>
        </Stack>
        {children}
      </Box>
    </Stack>
  );
};

export default Onboarding;
